/**
 * AdminPromptTemplatesPage - konfigurera AI-promptmallar (globalt + per provider)
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import MessageDialog from '../components/shared/MessageDialog';
import { useAuth } from '../context/AuthContext';

const TEMPLATE_LABELS = {
  generation: 'Generering',
  validation: 'Validering',
  ambiguity: 'Tvetydighet',
  proposed_edits: 'Förbättringsförslag'
};

const TEMPLATE_HELP = {
  generation: 'Styr hur AI skapar nya frågor och vilket schema som ska returneras.',
  validation: 'Kontrollerar kvalitet, korrekthet och tidskänslighet.',
  ambiguity: 'Avgör om flera svar kan vara korrekta.',
  proposed_edits: 'Föreslår minimala ändringar för att rädda en fråga.'
};

const BASE_VARIANT_ID = 'base';
const BASE_VARIANT_LABEL = 'Bas';
const DEFAULT_VARIANT_WEIGHT = 100;

const DEFAULT_CONTEXT = {
  amount: 3,
  category: 'Allmän kunskap',
  ageGroup: 'adults',
  difficulty: 'medium',
  targetAudience: 'swedish',
  targetAudiences: 'swedish',
  answerInQuestionPrompt: '',
  freshnessPrompt: '',
  feedbackPrompt: ''
};

const sampleQuestion = {
  question_sv: 'Vilken svensk stad kallas ibland för "Lilla Rom"?',
  question_en: 'Which Swedish city is sometimes called "Little Rome"?',
  options_sv: ['Visby', 'Uppsala', 'Lund', 'Örebro'],
  options_en: ['Visby', 'Uppsala', 'Lund', 'Örebro'],
  correctOption: 0,
  explanation_sv: 'Visby har en välbevarad medeltida stadskärna.',
  explanation_en: 'Visby has a well-preserved medieval town center.',
  background_sv: 'Visby ligger på Gotland och är UNESCO-världsarv.',
  background_en: 'Visby is on Gotland and is a UNESCO World Heritage site.'
};

const sampleAnalysis = {
  issues: ['Flera alternativ kan tolkas som rätt.'],
  suggestions: ['Gör frågan mer specifik.'],
  blockingRules: ['Svar i frågan'],
  preferOptionFix: true
};

const normalizeVariant = (variant) => ({
  id: String(variant?.id || variant?.variantId || variant?.variant_id || ''),
  label: variant?.label ? String(variant.label) : '',
  systemPrompt: variant?.systemPrompt ? String(variant.systemPrompt) : '',
  userPrompt: variant?.userPrompt ? String(variant.userPrompt) : '',
  weight: Number.isFinite(Number(variant?.weight)) ? Number(variant.weight) : DEFAULT_VARIANT_WEIGHT,
  isActive: variant?.isActive !== false && variant?.is_active !== 0,
  isBase: variant?.id === BASE_VARIANT_ID || variant?.isBase === true
});

const ensureBaseVariant = (variants = []) => {
  const normalized = Array.isArray(variants)
    ? variants.map(normalizeVariant).filter((item) => item.id)
    : [];
  const baseIndex = normalized.findIndex((variant) => variant.id === BASE_VARIANT_ID);
  const baseVariant = baseIndex >= 0
    ? {
        ...normalized[baseIndex],
        id: BASE_VARIANT_ID,
        label: normalized[baseIndex].label || BASE_VARIANT_LABEL,
        isBase: true,
        systemPrompt: '',
        userPrompt: ''
      }
    : {
        id: BASE_VARIANT_ID,
        label: BASE_VARIANT_LABEL,
        systemPrompt: '',
        userPrompt: '',
        weight: DEFAULT_VARIANT_WEIGHT,
        isActive: true,
        isBase: true
      };
  const rest = normalized.filter((variant) => variant.id !== BASE_VARIANT_ID);
  return [baseVariant, ...rest];
};

const buildGlobalVariantsState = (templateTypes = [], source = {}) => {
  const next = {};
  templateTypes.forEach((type) => {
    next[type] = ensureBaseVariant(source?.[type] || []);
  });
  return next;
};

const buildProviderVariantsState = (providers = [], templateTypes = [], source = {}) => {
  const next = {};
  providers.forEach((provider) => {
    const providerId = provider.id;
    if (!source?.[providerId]) return;
    const providerEntry = {};
    templateTypes.forEach((type) => {
      providerEntry[type] = ensureBaseVariant(source?.[providerId]?.[type] || []);
    });
    next[providerId] = providerEntry;
  });
  return next;
};

const createVariant = () => {
  const rawId = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2, 10);
  return {
    id: `variant_${rawId.slice(0, 8)}`,
    label: '',
    systemPrompt: '',
    userPrompt: '',
    weight: DEFAULT_VARIANT_WEIGHT,
    isActive: true,
    isBase: false
  };
};

const extractPlaceholders = (text = '') => {
  const matches = String(text).match(/\{\{\s*([\w.-]+)\s*\}\}/g) || [];
  return Array.from(
    new Set(matches.map((match) => match.replace(/[{}]/g, '').trim()))
  );
};

const buildProviderTemplatesState = (providers = [], templateTypes = [], source = {}) => {
  const next = {};
  providers.forEach((provider) => {
    const providerId = provider.id;
    const providerTemplates = source?.[providerId] || {};
    next[providerId] = {};
    templateTypes.forEach((type) => {
      const entry = providerTemplates?.[type] || {};
      next[providerId][type] = {
        systemPrompt: entry.systemPrompt || '',
        userPrompt: entry.userPrompt || ''
      };
    });
  });
  return next;
};

const buildGlobalTemplatesState = (templateTypes = [], globalSource = {}, defaults = {}) => {
  const next = {};
  templateTypes.forEach((type) => {
    const entry = globalSource?.[type] || defaults?.[type] || {};
    next[type] = {
      systemPrompt: entry.systemPrompt || '',
      userPrompt: entry.userPrompt || ''
    };
  });
  return next;
};

const normalizeTemplateTypes = (types = []) => (
  Array.isArray(types) && types.length > 0 ? types : ['generation', 'validation', 'ambiguity', 'proposed_edits']
);

const formatSourceLabel = (source) => {
  if (source === 'provider') return 'Provider';
  if (source === 'global') return 'Global';
  if (typeof source === 'string' && source.startsWith('variant:')) {
    return `Variant (${source.split(':')[1] || 'okänd'})`;
  }
  return 'Default';
};

const AdminPromptTemplatesPage = () => {
  const { isSuperUser, currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [dialogConfig, setDialogConfig] = useState({ isOpen: false, title: '', message: '', type: 'info' });
  const [providers, setProviders] = useState([]);
  const [defaults, setDefaults] = useState({});
  const [templateTypes, setTemplateTypes] = useState(['generation']);
  const [globalTemplates, setGlobalTemplates] = useState({});
  const [providerTemplates, setProviderTemplates] = useState({});
  const [globalVariants, setGlobalVariants] = useState({});
  const [providerVariants, setProviderVariants] = useState({});
  const [selectedType, setSelectedType] = useState('generation');
  const [previewType, setPreviewType] = useState('generation');
  const [previewProvider, setPreviewProvider] = useState('');
  const [previewVariantId, setPreviewVariantId] = useState('');
  const [previewContext, setPreviewContext] = useState(DEFAULT_CONTEXT);
  const [previewQuestionJson, setPreviewQuestionJson] = useState('');
  const [previewAnalysisJson, setPreviewAnalysisJson] = useState('');
  const [previewResult, setPreviewResult] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [feedbackSnapshot, setFeedbackSnapshot] = useState(null);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackError, setFeedbackError] = useState(null);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/ai-prompts', {
        headers: {
          'Content-Type': 'application/json',
          'x-user-email': currentUser?.email || ''
        }
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Kunde inte ladda promptmallar.');
      }

      const normalizedTypes = normalizeTemplateTypes(data.templateTypes);
      setTemplateTypes(normalizedTypes);
      setDefaults(data.defaults || {});
      setProviders(data.providers || []);
      setSelectedType(normalizedTypes[0] || 'generation');
      setPreviewType(normalizedTypes[0] || 'generation');

      const globalState = buildGlobalTemplatesState(
        normalizedTypes,
        data.templates?.global,
        data.defaults
      );
      const providerState = buildProviderTemplatesState(
        data.providers,
        normalizedTypes,
        data.templates?.providers
      );
      setGlobalTemplates(globalState);
      setProviderTemplates(providerState);
      setGlobalVariants(buildGlobalVariantsState(
        normalizedTypes,
        data.templates?.variants?.global || {}
      ));
      setProviderVariants(buildProviderVariantsState(
        data.providers,
        normalizedTypes,
        data.templates?.variants?.providers || {}
      ));
    } catch (err) {
      setError(err.message || 'Kunde inte ladda promptmallar.');
    } finally {
      setLoading(false);
    }
  }, [currentUser?.email]);

  const loadFeedbackSnapshot = useCallback(async () => {
    setFeedbackLoading(true);
    setFeedbackError(null);
    try {
      const response = await fetch('/api/ai-prompts/feedback', {
        headers: {
          'Content-Type': 'application/json',
          'x-user-email': currentUser?.email || ''
        }
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Kunde inte hämta feedback.');
      }
      setFeedbackSnapshot(data);
    } catch (err) {
      setFeedbackError(err.message || 'Kunde inte hämta feedback.');
    } finally {
      setFeedbackLoading(false);
    }
  }, [currentUser?.email]);

  useEffect(() => {
    if (!isSuperUser) {
      navigate('/');
      return;
    }
    loadTemplates();
    loadFeedbackSnapshot();
  }, [isSuperUser, navigate, loadTemplates, loadFeedbackSnapshot]);

  const placeholderList = useMemo(() => {
    const defaultsForType = defaults?.[selectedType] || {};
    const globalForType = globalTemplates?.[selectedType] || {};
    const baseText = `${defaultsForType.systemPrompt || ''}\n${defaultsForType.userPrompt || ''}\n${globalForType.systemPrompt || ''}\n${globalForType.userPrompt || ''}`;
    return extractPlaceholders(baseText);
  }, [defaults, globalTemplates, selectedType]);

  const feedbackKey = useMemo(() => (
    previewType === 'validation' ? 'validation' : 'generation'
  ), [previewType]);
  const feedbackPromptValue = feedbackSnapshot?.[feedbackKey]?.prompt || '';
  const feedbackVariantScores = useMemo(() => (
    Array.isArray(feedbackSnapshot?.[feedbackKey]?.variants) ? feedbackSnapshot[feedbackKey].variants : []
  ), [feedbackKey, feedbackSnapshot]);

  const variantScoreByProvider = useMemo(() => {
    const map = new Map();
    feedbackVariantScores.forEach((score) => {
      const providerId = score.provider ? String(score.provider).toLowerCase() : '';
      const variantId = score.variantId ? String(score.variantId) : '';
      if (!variantId) return;
      map.set(`${providerId}:${variantId}`, score);
    });
    return map;
  }, [feedbackVariantScores]);

  const variantScoreAggregate = useMemo(() => {
    const map = new Map();
    feedbackVariantScores.forEach((score) => {
      const variantId = score.variantId ? String(score.variantId) : '';
      if (!variantId) return;
      const current = map.get(variantId) || { count: 0, weightedSum: 0 };
      const count = Number(score.count) || 0;
      const avgRating = Number(score.avgRating) || 0;
      map.set(variantId, {
        count: current.count + count,
        weightedSum: current.weightedSum + avgRating * count
      });
    });
    const result = new Map();
    map.forEach((value, key) => {
      result.set(key, {
        count: value.count,
        avgRating: value.count > 0 ? value.weightedSum / value.count : null
      });
    });
    return result;
  }, [feedbackVariantScores]);

  const getVariantStats = (providerId, variantId) => {
    if (!variantId) return null;
    if (providerId) {
      return variantScoreByProvider.get(`${String(providerId).toLowerCase()}:${variantId}`) || null;
    }
    return variantScoreAggregate.get(variantId) || null;
  };

  const previewVariants = useMemo(() => {
    const providerId = previewProvider ? String(previewProvider).toLowerCase() : null;
    if (providerId && providerVariants?.[providerId]?.[previewType]) {
      return providerVariants[providerId][previewType] || [];
    }
    return globalVariants?.[previewType] || [];
  }, [previewProvider, previewType, providerVariants, globalVariants]);

  useEffect(() => {
    setPreviewVariantId('');
  }, [previewProvider, previewType]);

  const handleGlobalChange = (type, field, value) => {
    setGlobalTemplates((prev) => ({
      ...prev,
      [type]: {
        ...(prev[type] || {}),
        [field]: value
      }
    }));
  };

  const handleProviderChange = (providerId, type, field, value) => {
    setProviderTemplates((prev) => ({
      ...prev,
      [providerId]: {
        ...(prev[providerId] || {}),
        [type]: {
          ...(prev[providerId]?.[type] || {}),
          [field]: value
        }
      }
    }));
  };

  const handleToggleProviderOverride = (providerId, type, field) => {
    setProviderTemplates((prev) => {
      const currentValue = prev?.[providerId]?.[type]?.[field] || '';
      const defaultValue = globalTemplates?.[type]?.[field] || defaults?.[type]?.[field] || '';
      const nextValue = currentValue ? '' : defaultValue;
      return {
        ...prev,
        [providerId]: {
          ...(prev[providerId] || {}),
          [type]: {
            ...(prev[providerId]?.[type] || {}),
            [field]: nextValue
          }
        }
      };
    });
  };

  const updateVariantList = (list, variantId, field, value) => (
    list.map((variant) => {
      if (variant.id !== variantId) return variant;
      return {
        ...variant,
        [field]: value
      };
    })
  );

  const handleVariantChange = (scope, providerId, type, variantId, field, value) => {
    if (scope === 'global') {
      setGlobalVariants((prev) => ({
        ...prev,
        [type]: updateVariantList(prev?.[type] || [], variantId, field, value)
      }));
      return;
    }

    setProviderVariants((prev) => ({
      ...prev,
      [providerId]: {
        ...(prev[providerId] || {}),
        [type]: updateVariantList(prev?.[providerId]?.[type] || [], variantId, field, value)
      }
    }));
  };

  const handleAddVariant = (scope, providerId, type) => {
    const newVariant = createVariant();
    if (scope === 'global') {
      setGlobalVariants((prev) => ({
        ...prev,
        [type]: [...(prev?.[type] || []), newVariant]
      }));
      return;
    }
    setProviderVariants((prev) => ({
      ...prev,
      [providerId]: {
        ...(prev[providerId] || {}),
        [type]: [...(prev?.[providerId]?.[type] || ensureBaseVariant(globalVariants?.[type] || [])), newVariant]
      }
    }));
  };

  const handleRemoveVariant = (scope, providerId, type, variantId) => {
    if (variantId === BASE_VARIANT_ID) return;
    if (scope === 'global') {
      setGlobalVariants((prev) => ({
        ...prev,
        [type]: (prev?.[type] || []).filter((variant) => variant.id !== variantId)
      }));
      return;
    }
    setProviderVariants((prev) => ({
      ...prev,
      [providerId]: {
        ...(prev[providerId] || {}),
        [type]: (prev?.[providerId]?.[type] || []).filter((variant) => variant.id !== variantId)
      }
    }));
  };

  const handleEnableProviderVariants = (providerId) => {
    const clone = {};
    templateTypes.forEach((type) => {
      const list = ensureBaseVariant(globalVariants?.[type] || []);
      clone[type] = list.map((variant) => ({
        ...variant,
        systemPrompt: variant.systemPrompt || '',
        userPrompt: variant.userPrompt || ''
      }));
    });
    setProviderVariants((prev) => ({
      ...prev,
      [providerId]: clone
    }));
  };

  const handleResetProviderVariants = (providerId) => {
    setProviderVariants((prev) => {
      const next = { ...prev };
      delete next[providerId];
      return next;
    });
  };

  const handleResetGlobal = (type) => {
    const entry = defaults?.[type] || {};
    setGlobalTemplates((prev) => ({
      ...prev,
      [type]: {
        systemPrompt: entry.systemPrompt || '',
        userPrompt: entry.userPrompt || ''
      }
    }));
  };

  const handleResetProvider = (providerId, type) => {
    setProviderTemplates((prev) => ({
      ...prev,
      [providerId]: {
        ...(prev[providerId] || {}),
        [type]: {
          systemPrompt: '',
          userPrompt: ''
        }
      }
    }));
  };

  const buildSavePayload = () => {
    const globalPayload = {};
    templateTypes.forEach((type) => {
      const entry = globalTemplates?.[type] || {};
      globalPayload[type] = {
        systemPrompt: String(entry.systemPrompt || '').trim(),
        userPrompt: String(entry.userPrompt || '').trim()
      };
    });

    const providersPayload = {};
    Object.entries(providerTemplates || {}).forEach(([providerId, templates]) => {
      const providerEntry = {};
      templateTypes.forEach((type) => {
        const entry = templates?.[type] || {};
        const systemPrompt = String(entry.systemPrompt || '').trim();
        const userPrompt = String(entry.userPrompt || '').trim();
        if (systemPrompt || userPrompt) {
          providerEntry[type] = {
            systemPrompt: systemPrompt || null,
            userPrompt: userPrompt || null
          };
        }
      });
      if (Object.keys(providerEntry).length > 0) {
        providersPayload[providerId] = providerEntry;
      }
    });

    const normalizeVariantPayload = (variants = []) => (
      (variants || []).map((variant) => ({
        id: variant.id,
        label: String(variant.label || '').trim(),
        systemPrompt: String(variant.systemPrompt || '').trim(),
        userPrompt: String(variant.userPrompt || '').trim(),
        weight: Number.isFinite(Number(variant.weight)) ? Number(variant.weight) : DEFAULT_VARIANT_WEIGHT,
        isActive: variant.isActive !== false
      }))
    );

    const variantsPayload = {
      global: {},
      providers: {}
    };
    templateTypes.forEach((type) => {
      variantsPayload.global[type] = normalizeVariantPayload(globalVariants?.[type] || []);
    });
    Object.entries(providerVariants || {}).forEach(([providerId, templates]) => {
      const providerEntry = {};
      templateTypes.forEach((type) => {
        providerEntry[type] = normalizeVariantPayload(templates?.[type] || []);
      });
      variantsPayload.providers[providerId] = providerEntry;
    });

    return {
      global: globalPayload,
      providers: providersPayload,
      variants: variantsPayload
    };
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const response = await fetch('/api/ai-prompts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-email': currentUser?.email || ''
        },
        body: JSON.stringify({
          templates: buildSavePayload()
        })
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Kunde inte spara promptmallar.');
      }
      setDialogConfig({
        isOpen: true,
        title: 'Sparat!',
        message: 'Promptmallarna är uppdaterade.',
        type: 'success'
      });
      await loadTemplates();
    } catch (err) {
      setError(err.message || 'Kunde inte spara promptmallar.');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewResult(null);

    try {
      const context = {
        amount: Number(previewContext.amount) || 3,
        category: previewContext.category || 'Allmän kunskap',
        ageGroup: previewContext.ageGroup || 'adults',
        difficulty: previewContext.difficulty || 'medium',
        targetAudience: previewContext.targetAudience || 'swedish',
        targetAudiences: String(previewContext.targetAudiences || previewContext.targetAudience || 'swedish')
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean),
        answerInQuestionPrompt: previewContext.answerInQuestionPrompt || '',
        freshnessPrompt: previewContext.freshnessPrompt || '',
        feedbackPrompt: previewContext.feedbackPrompt || ''
      };

      if (previewQuestionJson.trim().length > 0) {
        context.question = JSON.parse(previewQuestionJson);
      }
      if (previewAnalysisJson.trim().length > 0) {
        context.analysis = JSON.parse(previewAnalysisJson);
      }

      const response = await fetch('/api/ai-prompts/preview', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-email': currentUser?.email || ''
        },
        body: JSON.stringify({
          type: previewType,
          provider: previewProvider || null,
          variantId: previewVariantId || null,
          context
        })
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Kunde inte förhandsvisa prompt.');
      }
      setPreviewResult(data);
    } catch (err) {
      setPreviewError(err.message || 'Kunde inte förhandsvisa prompt.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleApplyFeedbackPrompt = () => {
    if (!feedbackPromptValue) return;
    setPreviewContext((prev) => ({ ...prev, feedbackPrompt: feedbackPromptValue }));
  };

  const handleInsertSampleQuestion = () => {
    setPreviewQuestionJson(JSON.stringify(sampleQuestion, null, 2));
  };

  const handleInsertSampleAnalysis = () => {
    setPreviewAnalysisJson(JSON.stringify(sampleAnalysis, null, 2));
  };

  if (!isSuperUser) {
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-white">
        <Header title="AI-promptmallar" />
        <div className="flex items-center justify-center pt-32 text-slate-400">Laddar promptmallar...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <Header title="AI-promptmallar" />
      <main className="max-w-6xl mx-auto px-4 py-6 pt-24 space-y-6">
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-5 space-y-3 text-sm text-slate-300">
          <h2 className="text-base font-semibold text-white">Så byggs prompten</h2>
          <p>
            Systemet sätter ihop varje prompt i två delar: <strong>system</strong> och <strong>user</strong>.
            Ordningen är <span className="text-slate-200">Default → Global → Provider‑override</span>.
            Du kan ändra globalt och sedan finjustera enskilda providers.
          </p>
          <p className="text-xs text-slate-400">
            A/B‑varianter väljs viktat. Om en provider saknar egna varianter används de globala.
          </p>
          <p className="text-xs text-slate-500">
            Tips: håll mallarna stabila och använd placeholders (t.ex. <code>{'{{category}}'}</code>) i stället för hårdkodad text.
          </p>
        </div>

        {error && (
          <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-200">
            {error}
          </div>
        )}

        <section className="rounded-xl border border-slate-800 bg-slate-900/60 p-5 space-y-4">
          <div className="flex flex-wrap items-center gap-3 justify-between">
            <div>
              <h3 className="text-lg font-semibold text-white">Malltyp</h3>
              <p className="text-sm text-slate-400">{TEMPLATE_HELP[selectedType]}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {templateTypes.map((type) => (
                <button
                  key={type}
                  onClick={() => setSelectedType(type)}
                  className={`px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
                    selectedType === type
                      ? 'bg-cyan-500 text-black'
                      : 'bg-slate-800 text-slate-200 hover:bg-slate-700'
                  }`}
                >
                  {TEMPLATE_LABELS[type] || type}
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-slate-800 bg-slate-950/60 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-cyan-200">Global mall</h4>
              <button
                onClick={() => handleResetGlobal(selectedType)}
                className="text-xs text-slate-300 hover:text-white"
              >
                Återställ till default
              </button>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-xs text-slate-400">System‑prompt</label>
                <textarea
                  value={globalTemplates?.[selectedType]?.systemPrompt || ''}
                  onChange={(e) => handleGlobalChange(selectedType, 'systemPrompt', e.target.value)}
                  rows={8}
                  className="mt-2 w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-sm text-slate-100 focus:ring-2 focus:ring-cyan-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400">User‑prompt</label>
                <textarea
                  value={globalTemplates?.[selectedType]?.userPrompt || ''}
                  onChange={(e) => handleGlobalChange(selectedType, 'userPrompt', e.target.value)}
                  rows={8}
                  className="mt-2 w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-sm text-slate-100 focus:ring-2 focus:ring-cyan-500 focus:outline-none"
                />
              </div>
            </div>
            {placeholderList.length > 0 && (
              <div className="text-xs text-slate-400">
                Platshållare: {placeholderList.map((item) => (
                  <span key={item} className="inline-block mr-2 text-slate-200">
                    {`{{${item}}}`}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-lg border border-slate-800 bg-slate-950/60 p-4 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h4 className="text-sm font-semibold text-slate-200">A/B‑varianter (globalt)</h4>
                <p className="text-xs text-slate-400">
                  Varianter väljs viktat. Bas‑varianten återanvänder mallarna ovan.
                </p>
              </div>
              <button
                onClick={() => handleAddVariant('global', null, selectedType)}
                className="text-xs px-3 py-1 rounded-lg bg-slate-800 text-slate-200 hover:bg-slate-700"
              >
                Lägg till variant
              </button>
            </div>
            <div className="space-y-3">
              {(globalVariants?.[selectedType] || []).map((variant) => {
                const stats = getVariantStats(null, variant.id);
                const isBase = variant.id === BASE_VARIANT_ID || variant.isBase;
                return (
                  <div key={variant.id} className="rounded-lg border border-slate-800 bg-slate-900/50 p-3 space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <div className="text-sm font-semibold text-slate-100">
                          {variant.label || (isBase ? BASE_VARIANT_LABEL : variant.id)}
                          {isBase ? ' · bas' : ''}
                        </div>
                        <div className="text-xs text-slate-500">ID: {variant.id}</div>
                      </div>
                      {!isBase && (
                        <button
                          onClick={() => handleRemoveVariant('global', null, selectedType, variant.id)}
                          className="text-xs text-rose-200 hover:text-rose-100"
                        >
                          Ta bort
                        </button>
                      )}
                    </div>
                    <div className="grid gap-3 md:grid-cols-4">
                      <div>
                        <label className="text-xs text-slate-400">Label</label>
                        <input
                          value={variant.label || ''}
                          onChange={(e) => handleVariantChange('global', null, selectedType, variant.id, 'label', e.target.value)}
                          className="mt-2 w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-xs text-white"
                          placeholder="T.ex. Kortare intro"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-400">Vikt</label>
                        <input
                          type="number"
                          value={variant.weight ?? DEFAULT_VARIANT_WEIGHT}
                          onChange={(e) => handleVariantChange(
                            'global',
                            null,
                            selectedType,
                            variant.id,
                            'weight',
                            Number(e.target.value)
                          )}
                          className="mt-2 w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-xs text-white"
                        />
                      </div>
                      <div className="flex items-center gap-2 mt-6">
                        <input
                          id={`variant-active-${variant.id}`}
                          type="checkbox"
                          checked={variant.isActive !== false}
                          onChange={(e) => handleVariantChange(
                            'global',
                            null,
                            selectedType,
                            variant.id,
                            'isActive',
                            e.target.checked
                          )}
                        />
                        <label htmlFor={`variant-active-${variant.id}`} className="text-xs text-slate-300">
                          Aktiv
                        </label>
                      </div>
                      <div className="text-xs text-slate-400 mt-6">
                        {stats && Number.isFinite(stats.avgRating)
                          ? `Betyg ${stats.avgRating.toFixed(1)} · ${stats.count} betyg`
                          : 'Inga betyg ännu'}
                      </div>
                    </div>
                    {!isBase ? (
                      <div className="grid gap-4 md:grid-cols-2">
                        <div>
                          <label className="text-xs text-slate-400">System‑prompt</label>
                          <textarea
                            value={variant.systemPrompt || ''}
                            onChange={(e) => handleVariantChange(
                              'global',
                              null,
                              selectedType,
                              variant.id,
                              'systemPrompt',
                              e.target.value
                            )}
                            rows={6}
                            className="mt-2 w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-xs text-slate-100"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-slate-400">User‑prompt</label>
                          <textarea
                            value={variant.userPrompt || ''}
                            onChange={(e) => handleVariantChange(
                              'global',
                              null,
                              selectedType,
                              variant.id,
                              'userPrompt',
                              e.target.value
                            )}
                            rows={6}
                            className="mt-2 w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-xs text-slate-100"
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="text-xs text-slate-400">
                        Bas‑varianten använder de globala mallarna ovan (ingen egen prompttext).
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-slate-200">Provider‑överrides</h4>
            <div className="grid gap-4">
              {providers.map((provider) => {
                const providerId = provider.id;
                const providerLabel = provider.label || providerId;
                const providerEntry = providerTemplates?.[providerId]?.[selectedType] || {};
                const systemOverrideEnabled = Boolean(providerEntry.systemPrompt);
                const userOverrideEnabled = Boolean(providerEntry.userPrompt);
                const providerVariantList = providerVariants?.[providerId]?.[selectedType] || null;
                return (
                  <div key={providerId} className="rounded-lg border border-slate-800 bg-slate-950/60 p-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="text-sm font-semibold text-white">{providerLabel}</div>
                      <button
                        onClick={() => handleResetProvider(providerId, selectedType)}
                        className="text-xs text-slate-300 hover:text-white"
                      >
                        Rensa overrides
                      </button>
                    </div>
                    <div className="mt-3 grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-slate-400">System‑prompt override</span>
                          <button
                            onClick={() => handleToggleProviderOverride(providerId, selectedType, 'systemPrompt')}
                            className="text-xs text-cyan-200 hover:text-cyan-100"
                          >
                            {systemOverrideEnabled ? 'Använd global' : 'Aktivera'}
                          </button>
                        </div>
                        <textarea
                          value={providerEntry.systemPrompt || ''}
                          onChange={(e) => handleProviderChange(providerId, selectedType, 'systemPrompt', e.target.value)}
                          rows={6}
                          disabled={!systemOverrideEnabled}
                          className="w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-sm text-slate-100 focus:ring-2 focus:ring-cyan-500 focus:outline-none disabled:opacity-60"
                        />
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-slate-400">User‑prompt override</span>
                          <button
                            onClick={() => handleToggleProviderOverride(providerId, selectedType, 'userPrompt')}
                            className="text-xs text-cyan-200 hover:text-cyan-100"
                          >
                            {userOverrideEnabled ? 'Använd global' : 'Aktivera'}
                          </button>
                        </div>
                        <textarea
                          value={providerEntry.userPrompt || ''}
                          onChange={(e) => handleProviderChange(providerId, selectedType, 'userPrompt', e.target.value)}
                          rows={6}
                          disabled={!userOverrideEnabled}
                          className="w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-sm text-slate-100 focus:ring-2 focus:ring-cyan-500 focus:outline-none disabled:opacity-60"
                        />
                      </div>
                    </div>
                    <div className="mt-4 rounded-lg border border-slate-800 bg-slate-900/40 p-3 space-y-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="text-xs font-semibold text-slate-200">A/B‑varianter för {providerLabel}</div>
                        {providerVariantList ? (
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => handleAddVariant('provider', providerId, selectedType)}
                              className="text-xs px-3 py-1 rounded-lg bg-slate-800 text-slate-200 hover:bg-slate-700"
                            >
                              Lägg till variant
                            </button>
                            <button
                              onClick={() => handleResetProviderVariants(providerId)}
                              className="text-xs text-slate-300 hover:text-white"
                            >
                              Återställ till global
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleEnableProviderVariants(providerId)}
                            className="text-xs px-3 py-1 rounded-lg bg-slate-800 text-slate-200 hover:bg-slate-700"
                          >
                            Skapa egna varianter
                          </button>
                        )}
                      </div>
                      {!providerVariantList && (
                        <div className="text-xs text-slate-400">
                          Inga egna varianter ännu. Denna provider använder de globala varianterna.
                        </div>
                      )}
                      {providerVariantList && (
                        <div className="space-y-3">
                          {providerVariantList.map((variant) => {
                            const stats = getVariantStats(providerId, variant.id);
                            const isBase = variant.id === BASE_VARIANT_ID || variant.isBase;
                            return (
                              <div key={`${providerId}-${variant.id}`} className="rounded-lg border border-slate-800 bg-slate-950/60 p-3 space-y-3">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                  <div>
                                    <div className="text-sm font-semibold text-slate-100">
                                      {variant.label || (isBase ? BASE_VARIANT_LABEL : variant.id)}
                                      {isBase ? ' · bas' : ''}
                                    </div>
                                    <div className="text-xs text-slate-500">ID: {variant.id}</div>
                                  </div>
                                  {!isBase && (
                                    <button
                                      onClick={() => handleRemoveVariant('provider', providerId, selectedType, variant.id)}
                                      className="text-xs text-rose-200 hover:text-rose-100"
                                    >
                                      Ta bort
                                    </button>
                                  )}
                                </div>
                                <div className="grid gap-3 md:grid-cols-4">
                                  <div>
                                    <label className="text-xs text-slate-400">Label</label>
                                    <input
                                      value={variant.label || ''}
                                      onChange={(e) => handleVariantChange(
                                        'provider',
                                        providerId,
                                        selectedType,
                                        variant.id,
                                        'label',
                                        e.target.value
                                      )}
                                      className="mt-2 w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-xs text-white"
                                      placeholder="T.ex. Striktare"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs text-slate-400">Vikt</label>
                                    <input
                                      type="number"
                                      value={variant.weight ?? DEFAULT_VARIANT_WEIGHT}
                                      onChange={(e) => handleVariantChange(
                                        'provider',
                                        providerId,
                                        selectedType,
                                        variant.id,
                                        'weight',
                                        Number(e.target.value)
                                      )}
                                      className="mt-2 w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-xs text-white"
                                    />
                                  </div>
                                  <div className="flex items-center gap-2 mt-6">
                                    <input
                                      id={`variant-active-${providerId}-${variant.id}`}
                                      type="checkbox"
                                      checked={variant.isActive !== false}
                                      onChange={(e) => handleVariantChange(
                                        'provider',
                                        providerId,
                                        selectedType,
                                        variant.id,
                                        'isActive',
                                        e.target.checked
                                      )}
                                    />
                                    <label htmlFor={`variant-active-${providerId}-${variant.id}`} className="text-xs text-slate-300">
                                      Aktiv
                                    </label>
                                  </div>
                                  <div className="text-xs text-slate-400 mt-6">
                                    {stats && Number.isFinite(stats.avgRating)
                                      ? `Betyg ${stats.avgRating.toFixed(1)} · ${stats.count} betyg`
                                      : 'Inga betyg ännu'}
                                  </div>
                                </div>
                                {!isBase ? (
                                  <div className="grid gap-4 md:grid-cols-2">
                                    <div>
                                      <label className="text-xs text-slate-400">System‑prompt</label>
                                      <textarea
                                        value={variant.systemPrompt || ''}
                                        onChange={(e) => handleVariantChange(
                                          'provider',
                                          providerId,
                                          selectedType,
                                          variant.id,
                                          'systemPrompt',
                                          e.target.value
                                        )}
                                        rows={5}
                                        className="mt-2 w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-xs text-slate-100"
                                      />
                                    </div>
                                    <div>
                                      <label className="text-xs text-slate-400">User‑prompt</label>
                                      <textarea
                                        value={variant.userPrompt || ''}
                                        onChange={(e) => handleVariantChange(
                                          'provider',
                                          providerId,
                                          selectedType,
                                          variant.id,
                                          'userPrompt',
                                          e.target.value
                                        )}
                                        rows={5}
                                        className="mt-2 w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-xs text-slate-100"
                                      />
                                    </div>
                                  </div>
                                ) : (
                                  <div className="text-xs text-slate-400">
                                    Bas‑varianten använder provider‑ eller globalmall (ingen egen prompttext).
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="rounded-xl border border-slate-800 bg-slate-900/60 p-5 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-white">Preview / förhandsvisning</h3>
              <p className="text-sm text-slate-400">
                Se exakt vilka prompts som skickas (system + user) och var de kommer ifrån.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={loadFeedbackSnapshot}
                className="px-3 py-2 rounded-lg bg-slate-800 text-slate-200 text-xs hover:bg-slate-700"
                disabled={feedbackLoading}
              >
                {feedbackLoading ? 'Hämtar feedback...' : 'Uppdatera feedback'}
              </button>
              <button
                onClick={handlePreview}
                className="px-4 py-2 rounded-lg bg-emerald-500 text-black text-sm font-semibold hover:bg-emerald-400"
              >
                {previewLoading ? 'Kör preview...' : 'Kör preview'}
              </button>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <label className="text-xs text-slate-400">Malltyp</label>
              <select
                value={previewType}
                onChange={(e) => setPreviewType(e.target.value)}
                className="mt-2 w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-sm text-white"
              >
                {templateTypes.map((type) => (
                  <option key={type} value={type}>
                    {TEMPLATE_LABELS[type] || type}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-400">Provider</label>
              <select
                value={previewProvider}
                onChange={(e) => setPreviewProvider(e.target.value)}
                className="mt-2 w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-sm text-white"
              >
                <option value="">(Global mall)</option>
                {providers.map((provider) => (
                  <option key={provider.id} value={provider.id}>
                    {provider.label || provider.id}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-400">Variant</label>
              <select
                value={previewVariantId}
                onChange={(e) => setPreviewVariantId(e.target.value)}
                className="mt-2 w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-sm text-white"
              >
                <option value="">(Auto / viktad)</option>
                {(previewVariants || []).map((variant) => (
                  <option key={variant.id} value={variant.id}>
                    {variant.id === BASE_VARIANT_ID
                      ? `${variant.label || BASE_VARIANT_LABEL} · bas`
                      : `${variant.label || variant.id} · ${variant.id}`}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <label className="text-xs text-slate-400">Antal</label>
              <input
                type="number"
                value={previewContext.amount}
                onChange={(e) => setPreviewContext((prev) => ({ ...prev, amount: e.target.value }))}
                className="mt-2 w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-sm text-white"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400">Kategori</label>
              <input
                value={previewContext.category}
                onChange={(e) => setPreviewContext((prev) => ({ ...prev, category: e.target.value }))}
                className="mt-2 w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-sm text-white"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400">Åldersgrupp</label>
              <input
                value={previewContext.ageGroup}
                onChange={(e) => setPreviewContext((prev) => ({ ...prev, ageGroup: e.target.value }))}
                className="mt-2 w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-sm text-white"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400">Svårighet</label>
              <input
                value={previewContext.difficulty}
                onChange={(e) => setPreviewContext((prev) => ({ ...prev, difficulty: e.target.value }))}
                className="mt-2 w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-sm text-white"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400">Målgrupp (primär)</label>
              <input
                value={previewContext.targetAudience}
                onChange={(e) => setPreviewContext((prev) => ({ ...prev, targetAudience: e.target.value }))}
                className="mt-2 w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-sm text-white"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400">Målgrupper (komma‑separerat)</label>
              <input
                value={previewContext.targetAudiences}
                onChange={(e) => setPreviewContext((prev) => ({ ...prev, targetAudiences: e.target.value }))}
                className="mt-2 w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-sm text-white"
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="text-xs text-slate-400">Svar i frågan‑regler</label>
              <textarea
                value={previewContext.answerInQuestionPrompt}
                onChange={(e) => setPreviewContext((prev) => ({ ...prev, answerInQuestionPrompt: e.target.value }))}
                rows={3}
                className="mt-2 w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-sm text-white"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400">Aktualitet / bäst före</label>
              <textarea
                value={previewContext.freshnessPrompt}
                onChange={(e) => setPreviewContext((prev) => ({ ...prev, freshnessPrompt: e.target.value }))}
                rows={3}
                className="mt-2 w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-sm text-white"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs text-slate-400">Feedback‑sammanfattning</label>
              <button
                onClick={handleApplyFeedbackPrompt}
                disabled={!feedbackPromptValue}
                className="text-xs text-cyan-200 hover:text-cyan-100 disabled:text-slate-500"
              >
                Använd aktuell feedback
              </button>
            </div>
            <textarea
              value={previewContext.feedbackPrompt}
              onChange={(e) => setPreviewContext((prev) => ({ ...prev, feedbackPrompt: e.target.value }))}
              rows={3}
              className="mt-2 w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-sm text-white"
            />
          </div>

          <div className="rounded-lg border border-slate-800 bg-slate-950/60 p-4 space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-slate-200">Aktuell feedback som påverkar prompten</h4>
              {feedbackSnapshot?.updatedAt && (
                <span className="text-xs text-slate-500">
                  Uppdaterad: {new Date(feedbackSnapshot.updatedAt).toLocaleString('sv-SE')}
                </span>
              )}
            </div>
            {feedbackError && (
              <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-2 text-xs text-red-200">
                {feedbackError}
              </div>
            )}
            {!feedbackError && !feedbackSnapshot && (
              <div className="text-xs text-slate-400">Ingen feedback laddad ännu.</div>
            )}
            {feedbackSnapshot && (
              <div className="space-y-2 text-xs text-slate-300">
                <div>
                  Typ: <span className="text-slate-100">{feedbackKey === 'validation' ? 'Validering' : 'Generering'}</span>
                </div>
                <div className="flex flex-wrap gap-3 text-slate-400">
                  <span>Antal: {feedbackSnapshot?.[feedbackKey]?.summary?.total || 0}</span>
                  <span>Snittbetyg: {feedbackSnapshot?.[feedbackKey]?.summary?.avgRating ? feedbackSnapshot[feedbackKey].summary.avgRating.toFixed(1) : '—'}</span>
                  <span>Positiva: {feedbackSnapshot?.[feedbackKey]?.summary?.positiveCount || 0}</span>
                  <span>Negativa: {feedbackSnapshot?.[feedbackKey]?.summary?.negativeCount || 0}</span>
                </div>
                {feedbackSnapshot?.[feedbackKey]?.summary?.topIssues?.length > 0 && (
                  <div className="text-slate-400">
                    Vanliga problem: {feedbackSnapshot[feedbackKey].summary.topIssues.map((item) => `${item.issue} (${item.count})`).join(', ')}
                  </div>
                )}
                <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-3">
                  <div className="text-xs text-slate-400 mb-2">Feedback‑prompt som injiceras:</div>
                  <pre className="text-xs text-slate-100 whitespace-pre-wrap font-mono">
                    {feedbackPromptValue || 'Ingen feedback‑prompt just nu (för få betyg).'}
                  </pre>
                </div>
              </div>
            )}
          </div>

          {(previewType === 'validation' || previewType === 'ambiguity' || previewType === 'proposed_edits') && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-xs text-slate-400">Fråga (JSON)</label>
                <button
                  onClick={handleInsertSampleQuestion}
                  className="text-xs text-cyan-200 hover:text-cyan-100"
                >
                  Använd exempel
                </button>
              </div>
              <textarea
                value={previewQuestionJson}
                onChange={(e) => setPreviewQuestionJson(e.target.value)}
                rows={8}
                className="w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-xs text-slate-100 font-mono"
                placeholder="Lämna tomt för standardexempel"
              />
            </div>
          )}

          {previewType === 'proposed_edits' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-xs text-slate-400">Analys (JSON)</label>
                <button
                  onClick={handleInsertSampleAnalysis}
                  className="text-xs text-cyan-200 hover:text-cyan-100"
                >
                  Använd exempel
                </button>
              </div>
              <textarea
                value={previewAnalysisJson}
                onChange={(e) => setPreviewAnalysisJson(e.target.value)}
                rows={6}
                className="w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-xs text-slate-100 font-mono"
                placeholder="Lämna tomt för standardexempel"
              />
            </div>
          )}

          {previewError && (
            <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">
              {previewError}
            </div>
          )}

          {previewResult && (
            <div className="grid gap-4 md:grid-cols-2">
              {previewResult.variant && (
                <div className="md:col-span-2 rounded-lg border border-slate-800 bg-slate-950/60 p-3 text-xs text-slate-300">
                  Variant: <span className="text-slate-100">{previewResult.variant.label || previewResult.variant.id}</span>
                  {previewResult.variant.id ? ` · ${previewResult.variant.id}` : ''}
                  {previewResult.variant.scope ? ` · ${previewResult.variant.scope}` : ''}
                </div>
              )}
              <div className="rounded-lg border border-slate-800 bg-slate-950/60 p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-slate-200">System‑prompt</h4>
                  <span className="text-xs text-slate-400">Källa: {formatSourceLabel(previewResult.sources?.systemPrompt)}</span>
                </div>
                <pre className="text-xs text-slate-100 whitespace-pre-wrap font-mono">
                  {previewResult.systemPrompt}
                </pre>
              </div>
              <div className="rounded-lg border border-slate-800 bg-slate-950/60 p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-slate-200">User‑prompt</h4>
                  <span className="text-xs text-slate-400">Källa: {formatSourceLabel(previewResult.sources?.userPrompt)}</span>
                </div>
                <pre className="text-xs text-slate-100 whitespace-pre-wrap font-mono">
                  {previewResult.userPrompt}
                </pre>
              </div>
            </div>
          )}

          {previewResult?.unresolved?.length > 0 && (
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-200">
              Saknade placeholders: {previewResult.unresolved.join(', ')}
            </div>
          )}
        </section>

        <div className="flex flex-wrap items-center justify-end gap-3">
          <button
            onClick={loadTemplates}
            className="px-4 py-2 rounded-lg bg-slate-800 text-slate-200 text-sm hover:bg-slate-700"
          >
            Återställ ändringar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-cyan-500 text-black text-sm font-semibold hover:bg-cyan-400 disabled:opacity-60"
          >
            {saving ? 'Sparar...' : 'Spara mallar'}
          </button>
        </div>
      </main>

      <MessageDialog
        isOpen={dialogConfig.isOpen}
        onClose={() => setDialogConfig((prev) => ({ ...prev, isOpen: false }))}
        title={dialogConfig.title}
        message={dialogConfig.message}
        type={dialogConfig.type}
      />
    </div>
  );
};

export default AdminPromptTemplatesPage;
